import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import LegalEntityTypeForm from './LegalEntityTypeForm';
import CancelButton from '../../../shared/CancelButton';
import RowActionsMenu from '../../../shared/RowActionsMenu';
import { ActiveStatusPill } from '../../../shared/Pill';
import { PAGE_SIZE_OPTIONS, FALLBACK_PAGE_SIZE, useDefaultPageSize, getCachedDefaultPageSize, Pagination } from '../../../shared/pagination';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import '../../../shared/ListRowHover.css';
import '../../../shared/StyledInput.css';
import { useScreenAccess } from '../../../shared/PermissionsContext';
import { askConfirm } from '../../../shared/AppDialogs';

const CELL = { padding: '6px 8px', borderBottom: '1px solid #e3e7ee' };
const HEAD = { ...CELL, fontWeight: 600, color: '#5a6b85', textAlign: 'left', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.3 };

const TABS_ROW_STYLE = { display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap', borderBottom: '1px solid #b3bbc7' };
const TAB_WRAP_STYLE = { display: 'flex', alignItems: 'center', gap: 2, paddingBottom: 10 };

function tabColors(selected) {
  return {
    fontWeight: selected ? 700 : 400,
    color: selected ? '#222' : '#666',
    borderBottom: selected ? '2px solid #000' : '2px solid transparent',
  };
}

// The registers a Legal Entity Type is mapped to, with how many entity
// types each, e.g. "ABN Lookup (2)"; only the tab's register when one is
// chosen.
function mappingSummary(item, registers, registerKey) {
  const mapping = item.register_entity_types || {};
  return registers
    .filter((r) => (!registerKey || r.key === registerKey) && (mapping[r.key] || []).length)
    .map((r) => `${r.label} (${mapping[r.key].length})`)
    .join(', ');
}

// The register entity types a Legal Entity Type covers (ABN Lookup's
// "Australian Private Company" and so on), one row each, in its expanded
// row: a checked Business Number of one of them fills in a blank Legal
// Entity with this type. Adding or removing one saves straight away. One
// entity type sits on one active Legal Entity Type, so the Add list shows
// one already on another as taken.
function RegisterEntityTypePanel({ legalEntityType, registers: allRegisters, registerKey, canUpdate, showError, dismissError, onChanged }) {
  const registers = registerKey ? allRegisters.filter((r) => r.key === registerKey) : allRegisters;
  const [others, setOthers] = useState([]);
  const [saving, setSaving] = useState(false);
  const mapping = legalEntityType.register_entity_types || {};

  useEffect(() => {
    axios.get('/api/legal-entity-types', { params: { status: 'active' } })
      .then((res) => setOthers((res.data.data || []).filter((t) => t.id !== legalEntityType.id)))
      .catch(() => setOthers([]));
  }, [legalEntityType.id]);

  const save = async (next) => {
    setSaving(true);
    try {
      await axios.put(`/api/legal-entity-types/${legalEntityType.id}`, { name: legalEntityType.name, register_entity_types: next });
      dismissError();
      onChanged();
    } catch (err) {
      showError(err, 'Save failed');
    }
    setSaving(false);
  };
  const add = (register, code) => save({ ...mapping, [register]: [...(mapping[register] || []), code] });
  const remove = (register, code) => save({ ...mapping, [register]: (mapping[register] || []).filter((c) => c !== code) });

  const ownerOf = (register, code) => others.find((o) => ((o.register_entity_types || {})[register] || []).includes(code));
  const rows = registers.flatMap((r) => r.entityTypes
    .filter((t) => (mapping[r.key] || []).includes(t.code))
    .map((t) => ({ register: r, type: t })));

  return (
    <div style={{ padding: '4px 16px 16px 48px', background: '#f7f9fc' }}>
      <div style={{ fontSize: 13, color: '#5a6b85', margin: '6px 0 8px' }}>
        A business whose Business Number the register reports as one of these gets this Legal Entity when it has none.
      </div>
      <table style={{ width: '100%', maxWidth: 760, borderCollapse: 'collapse', fontSize: 13, background: '#fff', border: '1px solid #e3e7ee', borderRadius: 8 }}>
        <thead>
          <tr>
            <th style={{ ...HEAD, width: '25%' }}>Register</th>
            <th style={HEAD}>Entity type</th>
            <th style={{ ...HEAD, width: 70 }}>Code</th>
            <th style={{ ...HEAD, width: 50 }}></th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={4} style={{ ...CELL, color: '#9aa5b5' }}>No entity types yet.</td></tr>
          )}
          {rows.map(({ register, type }) => (
            <tr key={`${register.key}-${type.code}`}>
              <td style={{ ...CELL, color: '#5a6b85' }}>{register.label}</td>
              <td style={CELL}>{type.name}</td>
              <td style={{ ...CELL, color: '#5a6b85' }}>{type.code}</td>
              <td style={{ ...CELL, textAlign: 'center' }}>
                {canUpdate && (
                  <RowActionsMenu items={[{ key: 'remove', label: 'Remove', danger: true, disabled: saving, onClick: () => remove(register.key, type.code) }]} />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {canUpdate && legalEntityType.is_active && (
        <div style={{ marginTop: 10 }}>
          <select
            className="styled-input"
            value=""
            disabled={saving}
            onChange={(e) => { const [register, code] = e.target.value.split(':'); if (code) add(register, code); }}
            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 13, minWidth: 320 }}
          >
            <option value="">Add entity type…</option>
            {registers.map((r) => (
              <optgroup key={r.key} label={r.label}>
                {r.entityTypes.filter((t) => !(mapping[r.key] || []).includes(t.code)).map((t) => {
                  const owner = ownerOf(r.key, t.code);
                  return (
                    <option key={t.code} value={`${r.key}:${t.code}`} disabled={!!owner}>
                      {t.name}{owner ? ` (on ${owner.name})` : ''}
                    </option>
                  );
                })}
              </optgroup>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}

function LegalEntityTypeTable() {
  const { canCreate, canUpdate } = useScreenAccess();
  const navigate = useNavigate();
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [showForm, setShowForm] = useState(false);
  // Merge into: the row being merged, and the type it goes into.
  const [mergeItem, setMergeItem] = useState(null);
  const [mergeIntoId, setMergeIntoId] = useState('');
  const [mergeTargets, setMergeTargets] = useState([]);
  const [merging, setMerging] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 1 });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(() => getCachedDefaultPageSize() || FALLBACK_PAGE_SIZE);
  const [status, setStatus] = useState('active');
  const [loading, setLoading] = useState(false);
  const [registers, setRegisters] = useState([]);
  // Register tab: '' is All.
  const [registerKey, setRegisterKey] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  useDefaultPageSize(setLimit);

  useEffect(() => {
    axios.get('/api/legal-entity-types/registers')
      .then((res) => setRegisters(res.data.data || []))
      .catch(() => setRegisters([]));
  }, []);

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line
  }, [status, page, limit, registerKey]);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/legal-entity-types', { params: { status, page, limit, register: registerKey || undefined } });
      setRows(res.data.data || []);
      setPagination(res.data.pagination || { page, limit, total: 0, pages: 1 });
    } catch (err) {
      setRows([]);
      setPagination({ page, limit, total: 0, pages: 1 });
    }
    setLoading(false);
  };

  const handleAdd = () => {
    setEditItem(null);
    setShowForm(true);
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setShowForm(true);
  };

  const handleDeactivate = async (item) => {
    if (!(await askConfirm(`Deactivate Legal Entity Type "${item.name}"?`, { confirmLabel: 'Deactivate' }))) return;
    try {
      await axios.patch(`/api/legal-entity-types/${item.id}/deactivate`);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Deactivate failed');
    }
  };

  const handleReactivate = async (item) => {
    try {
      await axios.patch(`/api/legal-entity-types/${item.id}/reactivate`);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Reactivate failed');
    }
  };

  const startMerge = (item) => {
    setMergeItem(item);
    setMergeIntoId('');
    axios.get('/api/legal-entity-types', { params: { status: 'active' } })
      .then((res) => setMergeTargets((res.data.data || []).filter((t) => t.id !== item.id)))
      .catch(() => setMergeTargets([]));
  };

  // Counts the businesses first, so the confirm says how many move.
  const handleMerge = async () => {
    const target = mergeTargets.find((t) => String(t.id) === String(mergeIntoId));
    if (!target) return;
    setMerging(true);
    try {
      const preview = await axios.post(`/api/legal-entity-types/${mergeItem.id}/merge`, { into_id: target.id, dry_run: true });
      const moved = preview.data.data.moved;
      const message = `Move ${moved} business${moved === 1 ? '' : 'es'} from ${mergeItem.name} to ${target.name}, add ${mergeItem.name}'s register entity types to ${target.name}, and deactivate ${mergeItem.name}?\n\nThis can't be undone in one step.`;
      if (await askConfirm(message, { confirmLabel: 'Merge' })) {
        await axios.post(`/api/legal-entity-types/${mergeItem.id}/merge`, { into_id: target.id });
        setMergeItem(null);
        setExpandedId(null);
        fetchRows();
        dismissError();
      }
    } catch (err) {
      showError(err, 'Merge failed');
    }
    setMerging(false);
  };

  const handleSave = async (data) => {
    setFormLoading(true);
    try {
      if (editItem) {
        await axios.put(`/api/legal-entity-types/${editItem.id}`, data);
      } else {
        await axios.post('/api/legal-entity-types', data);
      }
      setShowForm(false);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Save failed');
    }
    setFormLoading(false);
  };

  return (
    <div style={{ margin: '32px 24px 48px', background: '#fafafa', border: '1px solid #eceef1', borderRadius: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.04)', paddingBottom: 32 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 0' }}>
        <h2 style={{ margin: 0, fontWeight: 600, fontSize: 20 }}>Legal Entity Types</h2>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {canCreate && (
            <button
              onClick={handleAdd}
              disabled={showForm}
              title={showForm ? 'Finish editing first' : undefined}
              style={{ padding: '8px 18px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 500, cursor: showForm ? 'not-allowed' : 'pointer', opacity: showForm ? 0.6 : 1 }}
            >
              Add Legal Entity Type
            </button>
          )}
        </div>
      </div>
      <div style={{ ...TABS_ROW_STYLE, margin: '16px 24px 0' }}>
        {[{ key: '', label: 'All' }, ...registers.map((r) => ({ key: r.key, label: r.label }))].map((tab) => (
          <div key={tab.key || 'all'} style={{ ...TAB_WRAP_STYLE, ...tabColors(registerKey === tab.key) }}>
            <button
              type="button"
              onClick={() => { setRegisterKey(tab.key); setPage(1); setExpandedId(null); }}
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', color: 'inherit', fontWeight: 'inherit' }}
            >
              {tab.label}
            </button>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 10, margin: '16px 24px 0', alignItems: 'center', flexWrap: 'wrap' }}>
        <select className="styled-input" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14 }}>
          <option value="active">Active items</option>
          <option value="inactive">Deactivated items</option>
        </select>
      </div>

      {showForm && (
        <div style={{ background: '#f7f9fc', border: '1px solid #e3e7ee', padding: 20, margin: '18px 24px 0', borderRadius: 8 }}>
          <LegalEntityTypeForm
            key={editItem?.id ?? 'add'}
            initial={editItem}
            onSave={handleSave}
            onCancel={() => setShowForm(false)}
            loading={formLoading}
          />
        </div>
      )}

      {mergeItem && (
        <div style={{ background: '#f7f9fc', border: '1px solid #e3e7ee', padding: 20, margin: '18px 24px 0', borderRadius: 8 }}>
          <div style={{ fontSize: 14, marginBottom: 8 }}>
            Merge <strong>{mergeItem.name}</strong> into another Legal Entity Type. Its businesses and register entity types move across, and {mergeItem.name} is deactivated.
          </div>
          <select className="styled-input" value={mergeIntoId} onChange={(e) => setMergeIntoId(e.target.value)} disabled={merging} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14, minWidth: 260 }}>
            <option value="">Merge into…</option>
            {mergeTargets.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <div style={{ marginTop: 12 }}>
            <button
              type="button"
              onClick={handleMerge}
              disabled={!mergeIntoId || merging}
              style={{ padding: '8px 18px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 500, cursor: !mergeIntoId || merging ? 'not-allowed' : 'pointer', opacity: !mergeIntoId || merging ? 0.6 : 1 }}
            >
              {merging ? 'Merging…' : 'Merge'}
            </button>
            <CancelButton onClick={() => setMergeItem(null)} disabled={merging} style={{ marginLeft: 8 }} />
          </div>
        </div>
      )}

      <div style={{ margin: '24px 24px 0', background: '#fff', border: '1px solid #b3bbc7', borderRadius: 10, overflow: 'visible', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 16, tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '6%' }} />
            <col style={{ width: '36%' }} />
            <col style={{ width: '23%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '15%' }} />
            <col style={{ width: '10%' }} />
          </colgroup>
          <thead className="list-head" style={{ fontSize: 14 }}>
            <tr>
              <th style={{ padding: '12px 4px', fontWeight: 600, borderBottom: '1px solid #b3bbc7' }}></th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Name</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Register</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}>In Use</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}>Status</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={6} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>No results</td></tr>
            ) : rows.map((item) => {
              const summary = mappingSummary(item, registers, registerKey);
              return (
              <React.Fragment key={item.id}>
              <tr
                className="list-row"
                onClick={() => setExpandedId((cur) => (cur === item.id ? null : item.id))}
                style={{ cursor: 'pointer' }}
                title="Show register entity types"
              >
                <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                  <ChevronRight size={16} strokeWidth={2} style={{ color: '#9aa5b5', transform: expandedId === item.id ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
                </td>
                <td style={{ padding: '6px 8px', fontSize: 14, fontWeight: 600 }}>{item.name}</td>
                <td style={{ padding: '6px 8px', fontSize: 13, color: '#5a6b85' }}>{summary || '—'}</td>
                <td style={{ padding: '6px 8px', textAlign: 'center', fontSize: 13 }} onClick={(e) => e.stopPropagation()}>
                  {item.usage_count ? (
                    <button
                      type="button"
                      style={{ background: 'none', border: 'none', padding: 0, color: '#1976d2', textDecoration: 'underline', cursor: 'pointer', fontSize: 13 }}
                      title={`See which entities have Legal Entity "${item.name}"`}
                      onClick={() => navigate(`/market-intel-all-entities?legal_entity_type_id=${item.id}&legal_entity_type_name=${encodeURIComponent(item.name)}`)}
                    >
                      {item.usage_count}
                    </button>
                  ) : '—'}
                </td>
                <td style={{ padding: '6px 8px', textAlign: 'center', fontSize: 13 }}>
                  <ActiveStatusPill active={item.is_active} />
                </td>
                <td style={{ padding: '6px 8px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                  <RowActionsMenu
                    items={[
                      { key: 'edit', label: 'Edit', disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleEdit(item) },
                      ...(item.is_active && canUpdate ? [{ key: 'merge', label: 'Merge into…', disabled: showForm || !!mergeItem, disabledTitle: showForm || mergeItem ? 'Finish editing first' : undefined, onClick: () => startMerge(item) }] : []),
                      item.is_active
                        ? { key: 'deactivate', label: 'Deactivate', danger: true, disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleDeactivate(item) }
                        : { key: 'reactivate', label: 'Reactivate', disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleReactivate(item) },
                    ]}
                  />
                </td>
              </tr>
              {expandedId === item.id && (
                <tr>
                  <td colSpan={6} style={{ padding: 0 }}>
                    <RegisterEntityTypePanel legalEntityType={item} registers={registers} registerKey={registerKey} canUpdate={canUpdate} showError={showError} dismissError={dismissError} onChanged={fetchRows} />
                  </td>
                </tr>
              )}
              </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <Pagination
        pagination={pagination}
        setPage={setPage}
        totalLabel="Legal Entity Types"
        rightContent={(
          <select className="styled-input"
            value={limit}
            onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
            style={{ marginLeft: 8, padding: '2px 4px', border: 'none', background: 'none', fontSize: 14, fontWeight: 600, color: '#333', cursor: 'pointer' }}
          >
            {PAGE_SIZE_OPTIONS.map((sz) => <option key={sz} value={sz}>{sz} per page</option>)}
          </select>
        )}
      />
    </div>
  );
}

export default LegalEntityTypeTable;
